import { EventThresholds } from "../../helpers/constants";
import { DateTime } from "luxon";

// Data types for bathroom count
export interface IBathroomSpecific {
  indiv_outlier?: boolean | string | null;
  indiv_category?: string | null;
  outlier_category?: string | null;
  typ_high_1?: number | null;
  typ_high_2?: number | null;
  typ_low_1?: number | null;
  typ_low_2?: number | null;
  typ_max?: number | null;
  typ_min?: number | null;
  visits?: number | null;
}
export interface IBathroomEventData {
  date: string;
  day?: IBathroomSpecific | null;
  night?: IBathroomSpecific | null;
  total?: IBathroomSpecific | null;
}

export interface IBathroomEventArray {
  data: IBathroomEventData[];
  max_count: number;
  zone_id: string;
  zone_name?: string | null;
}

export interface ISleepData {
  date: string;
  gtb_time: number | null;
  uaa_time: number | null;
  duration: number | null;
  status: string;
}

// Data types for temperature index
export interface ITemperaturePoints {
  heat_index?: number | null;
  timestamp?: number | null;
}
export interface ITemperatureChartData {
  name: string;
  temperature_data: ITemperaturePoints[];
}

export interface IDailyTemperatures {
  current?: number | null;
  high?: number | null;
  low?: number | null;
  heat_index?: number | null;
  temperature?: number | null;
  humidity?: number | null;
  name: string;
}

export interface ITypicalChart {
  name: string;
  temperature?: number | null;
  threshold?: EventThresholds | null;
  threshold_temp?: number | null;
  typ_high_1?: number | null;
  typ_high_2?: number | null;
  typ_low_1?: number | null;
  typ_low_2?: number | null;
  typ_max?: number | null;
  typ_min?: number | null;
}

export interface ITemperatureData {
  chart_data: ITemperatureChartData[];
  daily_temps: IDailyTemperatures[];
  heat_index?: number | null;
  indices: number[];
  threshold?: EventThresholds | null;
  typical_chart: ITypicalChart;
}

// Data types for no morning motion (up and about)
export interface IWakeupData {
  date: string;
  wakeup_time?: string | null;
}

export interface IMorningMotionData {
  last_motion_time: number;
  local_time_end: string;
  local_time_start: string;
  wakeup_data: IWakeupData[];
  zone_name?: string | null;
}

// Data types for activity monitor day and night,help button, exterior door,
// night bathroom visit count and night bathroom aggregate time
export interface ISensors {
  sensor_id?: string | null;
  sensor_name?: string | null;
  sensor_type?: string | null;
}

export interface IActivityTime {
  start_time?: number | null;
  end_time?: number | null;
}

export interface IContact {
  sensor_id?: string | null;
  activity?: IActivityTime[];
}
export interface IActivityData {
  activity?: IActivityTime[];
  contact?: IContact[];
  contact_count?: IContact[];
  sensors?: ISensors[] | null;
  zone_id: string;
  zone_name?: string | null;
  zone_type: string;
}

export interface IMonitorData {
  activity_data?: IActivityData[] | null;
  last_event_motion_time?: string | null;
  last_motion_event_time?: string | null;
  last_motion_zone_name?: string | null;
  last_contact_event_time?: string | null;
  last_contact_zone_name?: string | null;
  last_event_update_time?: string | null;
  local_time_end?: string | null;
  local_time_start?: string | null;
  triggering_time?: string | null;
  threshold_value?: number | null;
  triggering_value?: number | null;
}

export interface IFallData {
  activity_data?: IActivityData[] | null;
  triggering_time?: string | null;
  zone_name?: string | null;
}

export interface IPERSData {
  activity_data?: IActivityData[] | null;
  last_event_motion_time?: string | null;
  last_motion_event_time?: string | null;
  last_motion_zone_name?: string | null;
  last_event_update_time?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  altitude?: number | null;
  accuracy?: number | null;
  type?: string | null;
  timestamp_location?: string | null;
  timestamp_alert?: string | null;
  event_id?: string | null;
}

export interface IUnitActivityData {
  activity_data?: IActivityData[] | null;
  last_motion_event_time?: string | null;
  last_motion_zone_name?: string | null;
  last_contact_event_time?: string | null;
  last_contact_zone_name?: string | null;
}

export interface IKitchenCountData {
  afternoon: number;
  date: string;
  evening: number;
  morning: number;
  night: number;
}

export interface IUnitKitchenData {
  activity_data?: IActivityData[] | null;
  fridge_count?: number | null;
  temperature?: number | null;
  last_motion_event_time?: string | null;
  weekly_count?: IKitchenCountData[] | null;
}

export interface INotificationData {
  date: string;
  activity: number;
  bathroom: number;
  door: number;
  help_fall: number;
  temperature: number;
}

export interface IUnitNotificationData {
  weekly_count: INotificationData[];
}

export interface IBPReading {
  systolic: number;
  diastolic: number;
  timestamp: string | number;
}

export interface IPulseReading {
  pulse: number;
  timestamp: string | number;
}

export interface IBloodPressureData {
  bp_readings: IBPReading[];
  pulse_readings: IPulseReading[];
}

export interface IBloodPressureThresholdsCrossed {
  systolic_max: boolean;
  systolic_min: boolean;
  diastolic_max: boolean;
  diastolic_min: boolean;
}

export interface IBloodPressureWarningData {
  systolic: number;
  diastolic: number;
  pulse: number;
  timestamp: string | number;
  thresholds_crossed: IBloodPressureThresholdsCrossed;
  bp_readings: IBPReading[];
  pulse_readings: IPulseReading[];
}

/** Parses a blood pressure timestamp (ISO string or epoch ms) into a Luxon DateTime */
export const parseBPTimestamp = (ts: string | number): DateTime => {
  if (typeof ts === "string") return DateTime.fromISO(ts);
  return DateTime.fromMillis(ts);
};

/** Converts a blood pressure timestamp to epoch milliseconds */
export const bpTimestampToMillis = (ts: string | number): number => {
  if (typeof ts === "string") return new Date(ts).getTime();
  return ts;
};
